/**
 * Created by Ning Jiang on 11/23/2016.
 */

import {IGameConfig} from "@nolimitcity/slot-game/bin/core/gameconfig/IGameConfig";
import {BetWinMode} from "@nolimitcity/slot-game/bin/core/gametype/BetWinMode";
import {SpinMode} from "@nolimitcity/slot-game/bin/core/gametype/SpinMode";
import {IReelSets} from "@nolimitcity/slot-game/bin/core/reel/reelset/IReelSets";
import {IWinRatios, WinRatioType} from "@nolimitcity/slot-game/bin/core/winpresentation/WinRatio";
import {Back, Ease} from "gsap";
import {HorribleHeroesGameAssets} from "./HorribleHeroesGameAssets";
import {IHorribleHeroesSymbolStates} from "./symbols/HorribleHeroesReelSymbol";
import {HorribleHeroesSoundConfig} from "./HorribleHeroesSoundConfig";
import {HorribleHeroesLayerConfig, IHorribleHeroesLayerConfig} from "./config/IHorribleHeroesLayerConfig";
import {IStackedReelSymbolsConfig} from "@nolimitcity/slot-game/bin/core/reelsymbol/StateReelSymbol";
import {HorribleHeroesGameMode} from "./HorribleHeroesGameMode";

declare module "@nolimitcity/slot-game/bin/core/winpresentation/WinRatio" {
    export enum WinRatioType {
        SUPER_SUPER_MEGA_WIN = "superSuperMegaWin",
        SUPER_DUPER_MEGA_WIN = "superDuperMegaWin"
    }
}

const temp = WinRatioType as any;
temp["SUPER_SUPER_MEGA_WIN"] = "superSuperMegaWin";
temp["SUPER_DUPER_MEGA_WIN"] = "superDuperMegaWin";

export interface IHorribleHeroesWinRatioConfig {
    readonly ratio: number;
    readonly text?: string;
    readonly duration: number; // In second.
    readonly type?: WinRatioType;
    readonly sound: string;
}

export interface IHorribleHeroesBigWinRatioConfig extends IHorribleHeroesWinRatioConfig {
    readonly level: number;
}

export interface IHorribleHeroesWinRatios extends IWinRatios {
    readonly normalWin: IHorribleHeroesWinRatioConfig[];
    readonly bigWin: IHorribleHeroesBigWinRatioConfig[];
}

export class HorribleHeroesGameConfig implements IGameConfig {
    public static instance: HorribleHeroesGameConfig = new HorribleHeroesGameConfig();

    BROWSER_FPS: number = 30;

    GAME_WIDTH: number = 1280;
    GAME_HEIGHT: number = 740;

    LAYERS: IHorribleHeroesLayerConfig = HorribleHeroesLayerConfig.getConfig();
    BET_WIN_MODE: BetWinMode = BetWinMode.BET_WAY;
    SPIN_MODE: SpinMode = SpinMode.SPIN;

    REEL_AREA_POS_X: number = 0;
    REEL_AREA_POS_Y: number = 73;

    REEL_AREA_WIDTH: number = 720;
    REEL_AREA_HEIGHT: number = 588;
    REEL_MIDPOINT_Y: number = this.REEL_AREA_POS_Y + this.REEL_AREA_HEIGHT / 2;
    REEL_AREA_MASK: boolean = true;
    HIGHMSYMS = ["M1", "M2", "M3", "M4", "M5", "MX"];
    LOWSYMS = ["L1", "L2", "L3", "L4", "L5"];
    SELECTED_INDEX: number = 1;
    SYMBOL_STATES: IHorribleHeroesSymbolStates = {
        normal: {
            prio: 0,
            keyword: "normal",
            texturesCreator: (symName: string, stateKey: string) => {
                return HorribleHeroesGameAssets.getSymbolStateTextures(symName);
            }
        },
        spin: {
            prio: 1,
            keyword: "spin",
            texturesCreator: (symName: string, stateKey: string) => {
                return HorribleHeroesGameAssets.getSymbolStateTextures(symName, stateKey);
            }
        },
        attention: {
            prio: 2,
            keyword: "attention",
            symbols: ["X", "S", "W"],
            texturesCreator: (symName: string, stateKey: string) => {
                return HorribleHeroesGameAssets.getSymbolStateTextures(symName);
            }
        },
        noWin: {
            prio: 3,
            keyword: "noWin",
            texturesCreator: (symName: string, stateKey: string) => {
                return HorribleHeroesGameAssets.getSymbolStateTextures(symName);
            }
        },
        win: {
            prio: 4,
            keyword: "win",
            texturesCreator: (symName: string, stateKey: string) => {
                return HorribleHeroesGameAssets.getSymbolStateTextures(symName);
            }
        },
        idle: {
            prio: 5,
            keyword: "Idle",
            symbols: ["S", "W"],
            texturesCreator: (symName: string, stateKey: string) => {
                return HorribleHeroesGameAssets.getSymbolStateTextures(symName);
            }
        },
        hidden: {
            prio: 6,
            keyword: "hidden"
        },
        //to disable symbol masking during spinning
        //new state for mayhem_spin is added
        //#395
        mayhem: {
            prio: 7,
            keyword: "mayhem",
            symbols: ["X"],
            texturesCreator: (symName: string, stateKey: string) => {
                return HorribleHeroesGameAssets.getSymbolStateTextures(symName, stateKey);
            }
        },
    };
    STACKED_SYMBOLS: IStackedReelSymbolsConfig = {
        "M1_3": {
            totalNum: [3, 3, 3, 3, 3],
            reels: [0, 1, 2, 3, 4]
        },
        "M1_2": {
            totalNum: [2, 2, 2, 2, 2],
            reels: [0, 1, 2, 3, 4]
        },
        "M2_3": {
            totalNum: [3, 3, 3, 3, 3],
            reels: [0, 1, 2, 3, 4]
        },
        "M3_3": {
            totalNum: [3, 3, 3, 3, 3],
            reels: [0, 1, 2, 3, 4]
        },
        "M4_3": {
            totalNum: [3, 3, 3, 3, 3],
            reels: [0, 1, 2, 3, 4]
        },
        "M5_3": {
            totalNum: [3, 3, 3, 3, 3],
            reels: [0, 1, 2, 3, 4]
        },
        "M2_2": {
            totalNum: [2, 2, 2, 2, 2],
            reels: [0, 1, 2, 3, 4]
        },
        "M3_2": {
            totalNum: [2, 2, 2, 2, 2],
            reels: [0, 1, 2, 3, 4]
        },
        "M4_2": {
            totalNum: [2, 2, 2, 2, 2],
            reels: [0, 1, 2, 3, 4]
        },
        "M5_2": {
            totalNum: [2, 2, 2, 2, 2],
            reels: [0, 1, 2, 3, 4]
        },
        "X_2": {
            totalNum: [2, 2, 2, 2, 2],
            reels: [0, 1, 2, 3, 4]
        },
        "X_3": {
            totalNum: [3, 3, 3, 3, 3],
            reels: [0, 1, 2, 3, 4]
        },
        "MX_2": {
            totalNum: [2, 2, 2, 2, 2],
            reels: [0, 1, 2, 3, 4]
        },
        "MX_3": {
            totalNum: [3, 3, 3, 3, 3],
            reels: [0, 1, 2, 3, 4]
        },
        "MX_4": {
            totalNum: [4, 4, 4, 4, 4],
            reels: [0, 1, 2, 3, 4]
        }
    };
    SYMBOL_WIDTH: number = 144;
    SYMBOL_HEIGHT: number = 147;
    SYMBOL_BOTTOM_PADDING: number = 0;
    REELS_NUM: number = 5;
    SPACE_BETWEEN_REELS: number = 0;
    REEL_DISPLAY_POSITIONS: [number, number][] = [
        [this.SYMBOL_WIDTH * 0.5, 0],
        [this.SYMBOL_WIDTH * 1.5 + this.SPACE_BETWEEN_REELS, 0],
        [this.SYMBOL_WIDTH * 2.5 + this.SPACE_BETWEEN_REELS * 2, 0],
        [this.SYMBOL_WIDTH * 3.5 + this.SPACE_BETWEEN_REELS * 3, 0],
        [this.SYMBOL_WIDTH * 4.5 + this.SPACE_BETWEEN_REELS * 4, 0]];
    SYMBOLS_NUM_IN_REEL: [number, number, number, number, number] = [0, 1, 4, 1, 4];

    REEL_SPIN_TIME: number = 0.9; // not used in this framework
    REEL_FAST_SPIN_SPIN_TIME: number = 0.2; // not used in this framework

    REEL_SPIN_START_SYMBOL_CHANGE_TO_SPIN_STATE_DURATION: number = 0.2;
    REEL_SPIN_START_SYMBOL_CHANGE_TO_SPIN_STATE_DELAY: number = 0.3;
    REEL_SPIN_START_EASE: Ease = Back.easeIn.config(4);
    REEL_SPIN_STOP_SYMBOL_CHANGE_FROM_SPIN_STATE_DURATION: number = 0.2;
    REEL_SPIN_STOP_SYMBOL_CHANGE_FROM_SPIN_STATE_DELAY: number = 0;
    REEL_SPIN_STOP_ELASTIC_CONFIG: [number, number] = [1.5, 0.95];
    REEL_SPIN_QUICK_STOP_ELASTIC_CONFIG: [number, number] = [1.2, 0.75];

    REEL_SPIN_START_SPEED: number = 0.3;
    REEL_SPIN_SPIN_SPEED: number = 0.04;
    REEL_NEAR_WIN_SPIN_SPEED: number = 0.03;
    REEL_SPIN_STOP_SPEED: number = 0.2;
    REEL_SPIN_QUICK_STOP_SPEED: number = 0.2;

    REEL_STOP_DELAY: number = 0.2;
    REEL_QUICK_STOP_DELAY: number = 0.05;
    REEL_NEAR_WIN_STOP_DELAY: number = 0.0; //This needs to be set to 0 if you want to control this in the gameroundmanager config.;
    REEL_FAST_SPIN_DELAY: number = 0.1;//#569 to increase delays in between each reelstop
    REEL_SETS: IReelSets = {
        "BASE_REELSET": [
            ["L3", "M2", "L5", "L4", "L1", "M5", "L2", "L4", "L3", "M4", "L1", "M3", "L4", "L2", "L2",
                "X", "L1", "L1", "M5", "M5", "L2", "M3", "L5", "L1", "L3", "L2", "M2", "M4", "L1", "L3",
                "L5", "L5", "L2", "L2", "L1", "L1", "L4", "M3", "L5", "L5", "L3", "L3", "L4", "L4", "L2",
                "L5", "L5", "M5", "L3", "L3", "M1", "L1", "L4", "L4", "L3", "L5", "M3", "L1", "M2", "L3",
                "L5", "L4", "L4", "M2", "L1", "L1", "M5", "M5", "L2", "L2", "L3", "L3", "L1", "L5", "L5",
                "L2", "L3", "M3", "L5", "S", "M5", "L1", "M4", "L2", "L4", "L3", "L3", "M2", "L5", "L2",
                "M4", "L3", "L1", "L5", "L5", "L4", "L3", "M3", "L2", "L5", "L3", "M2", "L5", "L4", "L1",
                "M5", "L2", "L4", "L3", "X", "L1", "M3", "L4", "L2", "L2", "L5", "L1", "L1", "M5", "M5",
                "L2", "M3", "L5", "L1", "L3", "L2", "M2", "M4", "L1", "L3", "L5", "L5", "L2", "L2", "L1",
                "L1", "L4", "M3", "L5", "L5", "L3", "L3", "L4", "L4", "L2", "L5", "L5", "M5", "L3", "L3",
                "M1", "L1", "L4", "L4", "L3", "L5", "M3", "L1", "M2", "L3", "L5", "L4", "L4", "M2", "L1",
                "L1", "M5", "M5", "L2", "L2", "L3", "L3", "L1", "L5", "L5", "L2", "L3", "M3", "L5", "M5",
                "L1", "M4", "L2", "L4", "L3", "L3", "M2", "L5", "L2", "M4", "L3", "L1", "L5", "L5", "L4",
                "L3", "M3", "L2", "L5", "M3", "L5", "L4", "M1", "L2", "L3", "L3", "L1", "M5", "M2", "L5",
                "L1", "L4", "L3", "L5", "L5", "S", "M5", "M4", "M4", "X", "L5", "L2", "L2", "L4", "L1",
                "L1", "M1", "L2", "L3", "L5", "L5", "M2", "L1", "L1", "L4", "L5", "L5", "M3", "L1", "M1",
                "L5", "L3", "M5", "L4", "L1", "L5", "L3", "L4", "S", "L5", "M2", "L3", "M1", "M1", "L5"],
            ["L4", "M4", "M4", "L2", "L5", "L4", "M2", "L2", "L5", "L4", "S", "M4", "L2", "L5", "L5",
                "L4", "X", "M2", "M4", "L2", "L5", "L5", "L4", "M4", "L2", "L5", "L5", "L4", "L4", "M4",
                "L2", "L5", "L4", "M4", "L2", "L2", "L5", "L4", "M4", "M2", "L2", "L2", "L4", "L4", "M4",
                "L5", "M2", "L2", "L4", "L4", "L5", "M4", "L2", "M2", "L5", "L4", "L2", "L2", "M4", "L5",
                "L4", "L4", "L2", "S", "M2", "L5", "L4", "L4", "L2", "M4", "L5", "L4", "L4", "L2", "M4",
                "L5", "L4", "L2", "M2", "L5", "L5", "S", "M4", "M4", "L2", "L4", "L5", "M4", "L2", "L2",
                "L4", "L4", "S", "L5", "L5", "M2", "L2", "L4", "L5", "M4", "L2", "L4", "L5", "L4", "M2",
                "L1", "L5", "L2", "L3", "M1", "L4", "S", "L2", "L1", "L5", "L5", "M2", "L2", "M3", "L4",
                "L3", "M4", "M3", "L4", "L4", "L2", "L2", "L3", "M4", "M4", "M3", "M3", "M5", "M5", "L2",
                "L5", "L1", "L3", "L3", "L4", "M2", "M5", "M3", "L2", "L5", "L4", "M5", "L3", "L3", "L5",
                "L2", "L2", "L4", "L4", "L3", "M5", "M5", "L2", "L4", "M3", "L3", "L2", "M2", "M2", "L4",
                "L3", "M5", "L2", "M4", "L3", "M5", "M5", "M2", "L2", "L3", "L3", "L4", "M2", "L1", "M3",
                "L3", "M2", "M5", "L5", "L3", "X", "M2", "L4", "L2", "L5", "M5", "W", "L4", "L1", "L1",
                "M2", "L3", "L3", "M1", "M1", "L5", "L2", "L2", "L4", "M5", "M3", "L2", "X", "L3", "L5",
                "M5", "M5", "M1", "M2", "L2", "L2", "L4", "M3", "L5", "M2", "L2", "W", "L4", "L3", "L1",
                "M2", "L4", "L5", "M5", "M2", "L2", "L4", "M1", "L5", "L2", "L2", "M5", "L4", "W", "L2",
                "L2", "L3", "M2", "M5", "M5", "L2", "L3", "W", "L1", "L2", "L5", "L3", "L4", "M5", "L5",
                "L1", "L3", "M2", "L4", "L5", "S", "L3", "L3", "L2", "L1", "M2", "L3", "X", "L2", "M3",
                "M2", "L1", "L1", "L2", "L4", "L5", "L3", "L3", "M2", "M5", "L2", "L1", "X", "L4", "M4",
                "L2", "M2", "L1", "L5", "M5", "L2", "L4", "M2", "M5", "L5", "L5", "L1", "L4", "L4", "M5",
                "L2", "M2", "M3", "M3", "M4", "X", "L4", "L3", "M5", "M4", "S", "L5", "M2", "M5", "M3",
                "M1", "L4", "L1", "M4", "W", "M3", "M1", "M2", "M5", "M4", "L5", "L4", "M3", "L1", "M1",
                "L4", "L2", "M5", "L3", "M2", "S", "M5", "M1", "M4", "L3", "L3", "L5", "M3", "M5", "L4",
                "L3", "M3", "M3", "M2", "W", "L3", "L3", "L1", "L1", "L4", "M5", "M4", "M3", "M1", "L3",
                "L4", "L4", "M2", "M1", "L1", "M3", "M2", "L2", "X", "L4", "L1", "L3", "M5", "M3"],
            ["L1", "M5", "M5", "L3", "L1", "M1", "M5", "L3", "L3", "L1", "M3", "M5", "L3", "L1", "M3",
                "M5", "L1", "L3", "L1", "M3", "M1", "L3", "M5", "L2", "L1", "M1", "S", "L3", "M3", "M5",
                "L1", "L3", "L3", "M1", "M1", "L1", "M5", "M5", "L3", "L1", "M1", "M5", "L3", "L3", "L1",
                "M3", "M5", "L3", "L1", "M3", "M5", "L1", "L3", "L1", "M3", "M1", "L3", "M5", "L2", "L1",
                "M1", "L5", "L3", "M3", "M5", "L1", "L3", "L3", "M1", "M1", "L1", "L3", "L1", "M3", "M4",
                "X", "L4", "L3", "M5", "M4", "S", "L5", "M2", "M5", "M3", "M1", "L4", "L1", "M4", "W",
                "M3", "M1", "M2", "M5", "M4", "L5", "L4", "M3", "L1", "M1", "L4", "L2", "M5", "L3", "M2",
                "S", "M5", "M1", "M4", "L3", "L3", "L5", "M3", "M5", "L4", "L3", "M3", "M3", "M2", "W",
                "L3", "L3", "L1", "L1", "L4", "M5", "M4", "M3", "M1", "L3", "L4", "L4", "M2", "M1", "L1",
                "M3", "M2", "L2", "X", "L4", "L1", "L3", "M5", "M3"],
            ["L1", "L1", "L2", "L2", "M4", "M4", "L5", "L4", "M5", "L2", "M3", "L5", "L4", "L3", "L2",
                "L1", "M1", "M4", "M3", "L1", "M1", "L3", "L5", "S", "L2", "L4", "L3", "L1", "L2", "L5",
                "M5", "M5", "L4", "L4", "L2", "L2", "L3", "L3", "L5", "M5", "L2", "L3", "L3", "L4", "M1",
                "L1", "L2", "L4", "M2", "L3", "L1", "L2", "L2", "M4", "X", "L1", "L3", "L5", "L2", "L4",
                "L4", "L1", "L3", "L2", "L5", "L1", "L1", "M4", "L3", "L2", "L1", "L1", "L4", "L5", "W",
                "M5", "L2", "M3", "L3", "L3", "L5", "M4", "M4", "L2", "L2", "L1", "L5", "M5", "M1", "L4",
                "L1", "M3", "L2", "L2", "L4", "L5", "L1", "L2", "L3", "M4", "L1", "L4", "L4", "M1", "M5",
                "L1", "L2", "L3", "L4", "L1", "L1", "M3", "L2", "M2", "L5", "L3", "M3", "L4", "M4", "L3",
                "L5", "L4", "L2", "M2", "L3", "L3", "M4", "L2", "L2", "L1", "M2", "L3", "M3", "W", "L4",
                "L4", "L5", "L5", "L2", "L1", "L4", "L5", "L3", "L2", "M3", "X", "L3", "L3", "M2", "L5",
                "M4", "L4", "L3", "L5", "W", "L1", "M3", "M3", "M4", "L5", "L2", "L4", "L3", "L5", "L2",
                "L2", "L1", "M4", "M5", "L4", "L2", "L2", "L3", "M4", "M2", "M3", "S", "L2", "M2", "L3",
                "X", "L4", "M3", "L3", "M1", "L2", "L5", "L1", "L3", "L3", "L2", "M4", "M1", "L3", "L3",
                "L5", "L1", "L4", "M3", "M3", "M2", "M2", "L5", "M4", "L3", "L3", "L2", "L2", "M3", "M1",
                "M5", "L3", "L1", "W", "L2", "L3", "M3", "M4", "L2", "L3", "M3", "L1", "L4", "M4", "M4",
                "L2", "M2", "L4", "M3", "L3", "L2", "M4", "L1", "L1", "L3", "L3", "W", "L2", "L4", "L4",
                "L3", "M4", "L1", "L1", "M2", "L2", "L3", "M3", "L4", "L4", "L2", "M5", "M5", "M3", "L3",
                "L5", "S", "L1", "M3", "L2", "M4", "M4", "L4", "M1", "L1", "L2", "L2", "M4", "L3", "L3"],
            ["L1", "L1", "S", "L5", "L5", "L2", "L1", "M1", "L4", "M5", "L3", "L4", "M3", "L1", "S",
                "L2", "L2", "M2", "L5", "L5", "L3", "L2", "M4", "L4", "S", "L1", "L2", "L2", "L3", "L3",
                "L4", "M3", "L2", "L3", "L1", "M1", "L5", "L2", "L4", "L4", "M5", "L1", "M2", "M3", "L4",
                "M1", "L3", "L3", "L2", "L4", "M4", "M3", "L2", "L3", "L3", "L4", "L5", "M4", "L2", "M1",
                "L3", "M3", "L4", "M4", "S", "M5", "M3", "M2", "M1", "L2", "L2", "L5", "L5", "L3", "M4",
                "M3", "M2", "L2", "L1", "M1", "X", "M5", "M2", "L5", "L5", "M1", "L2", "L3", "L4", "M4",
                "L1", "L1", "M1", "L3", "L3", "M5", "M5", "L5", "M3", "M2", "L1", "M4", "M4", "L5", "L5",
                "M2", "M2", "L3", "M5", "M3", "M3", "L5", "M4", "M5", "L4", "M3", "M1", "L2", "L1", "M4",
                "M1", "M3", "L3", "L3", "L5", "M1", "M5", "M4", "L1", "S", "M2", "M5", "L1", "L2", "M3",
                "M2", "M4", "M4", "L3", "M1", "M1", "M3", "M2", "L3", "L4", "M3", "L5", "L3", "L1", "L4",
                "M3", "L3", "M5", "L1", "L4", "L5", "M4", "L2", "M1", "L3", "M3", "L4", "M4", "S", "M5",
                "M3", "M2", "M1", "L2", "L2", "L5", "L5", "L3", "M4", "M3", "M2", "L2", "L1", "M1", "X",
                "M5", "M2", "L5", "L5", "M1", "L2", "L3", "L4", "M4", "L1", "L1", "M1", "L3", "L3", "M5",
                "M5", "L5", "M3", "M2", "L1", "M4", "M4", "L5", "L5", "M2", "M2", "L3", "M5", "M3", "M3",
                "L5", "M4", "M5", "L4", "M3", "M1", "L2", "L1", "M4", "M1", "M3", "L3", "L3", "L5", "M1",
                "M5", "M4", "L1", "L4", "M2", "M5", "L1", "L2", "M3", "M2", "M4", "M4", "L3", "M1"]
        ],
        "FREESPIN_LOW_REELSET": [
            ["L1", "M3", "L2", "L3", "M4", "L4", "M5", "M3", "L3", "L1", "M4", "M5", "L1", "M1", "L3",
                "M3", "L2", "L4", "L2", "M5", "M1", "L3", "M4", "L1", "M2", "L2", "M5", "L5", "L4", "M5",
                "L2", "L1", "M4", "L4", "M4", "L3", "M3", "M2", "L5", "M1", "L2", "M5", "M3", "L1", "L4",
                "L3", "M4", "L5", "X", "M3", "L2", "L2", "M4", "L4", "M2", "L2", "M5", "L4", "L1", "L3",
                "M1", "M2", "M4", "L4", "L3", "M5", "L5", "M2", "L2", "L1", "M4", "L5", "L3", "L4", "M5",
                "L1", "M3", "M2", "L5", "M5", "M1", "L1", "M4", "M5", "L4", "M5", "L1", "L4", "M2", "L3",
                "L5", "M5", "L4", "L2", "M3", "L1", "L3", "L4", "M5", "L2", "M4", "L1", "M1", "L2", "M3",
                "M5", "L3", "M2", "L4", "M4", "L2", "L1", "M2", "L3", "L5", "M5", "L2", "L4", "L1", "M1",
                "L3", "M2", "L2", "M3", "M4", "L3", "L2", "M5", "L5", "M2", "L1", "M1", "L3", "M3", "M5",
                "L5", "M4", "L4", "L3", "M4", "L1", "M2", "L3", "M3", "L2", "L4", "L1", "M5", "M2", "L5",
                "L2", "M3", "L5", "L1", "M4", "L4", "M1", "L3", "M3", "L5"],

            ["M3", "L2", "M4", "L1", "M1", "L5", "M2", "X", "M5", "L1", "L2", "L3", "M4", "M3", "L5",
                "M5", "L2", "M1", "L4", "M3", "L5", "M3", "L5", "M5", "L2", "M5", "L1", "M1", "L4", "M3",
                "L2", "M5", "L3", "M4", "M3", "L1", "M1", "L3", "M5", "M3", "L4", "M4", "M2", "L2", "M5",
                "M1", "L5", "L4", "M3", "L2", "M5", "L1", "L3", "M3", "M2", "L3", "M3", "L4", "M4", "M1",
                "L3", "M3", "L2", "M2", "M4", "L1", "M1", "M4", "L4", "M3", "M5", "L5", "M3", "L2", "L3",
                "M1", "L4", "M4", "L3", "L2", "L1", "L5", "L3", "M4", "L2", "L4", "X", "L1", "L5", "L3",
                "M5", "L1", "L4", "M1", "L5", "L3", "M3", "M5", "M4", "M1", "L4", "M3", "M4", "M2", "M3",
                "L1", "M1", "S", "L3", "L2", "L3", "L2", "L1", "M1", "M3", "L3", "M2", "M4", "L5", "L4",
                "L2", "L3", "L4", "M5", "L2", "L4", "L5", "L1", "X", "L3", "L4", "M2", "L2", "L4", "L5",
                "M2", "L3", "M3", "L2", "L1", "L4", "M4", "M3", "M5", "L5", "M3", "L2", "L3", "L4", "M4",
                "L3", "L2", "L1", "L5", "L3", "L2", "L4", "M2", "L1", "M1", "L3", "M5", "L1", "L4", "M1",
                "L5", "L3", "M3", "M5", "M4", "M1", "L4", "L3", "M2", "M3", "L1", "M1", "M4", "M2", "L3",
                "L2", "X", "L1", "M1", "M3", "L4", "L5", "L4", "M2", "L3", "L4", "M5", "L2", "L4", "L5",
                "L1", "L3", "L4", "M2", "L2", "L4", "L5", "L1", "L3", "M3", "L2", "L1", "L4"],

            ["M1", "L5", "M2", "L4", "L2", "M5", "L5", "L1", "L3", "M1", "L2", "M4", "L1", "M4", "L3",
                "M4", "M5", "L4", "M3", "L2", "L5", "M1", "L1", "L3", "L2", "M5", "L3", "M2", "L4", "M4",
                "L2", "M5", "L1", "M1", "M3", "L3", "L3", "M2", "L2", "M5", "M4", "M3", "L3", "L1", "L5",
                "L3", "L4", "L1", "L2", "L2", "M1", "M5", "M5", "L3", "L3", "L4", "L5", "M2", "L2", "M3",
                "M1", "M5", "L3", "L3", "M2", "L1", "L2", "L5", "L4", "M3", "M1", "L1", "L1", "M4", "M2",
                "L3", "L3", "L2", "L4", "M1", "M5", "L3", "L3", "M3", "L1", "L1", "M4", "M2", "L2", "M5",
                "M3", "L2", "M1", "M4", "L1", "L4", "M5", "L2", "M2", "M1", "X", "L3", "L2", "L5", "M1",
                "L3", "L4", "M5", "L1", "L5", "L3", "M4", "M3", "L4", "L2", "L1", "M2", "L3", "M3", "M3",
                "M4", "L3", "L2", "L4", "L1", "M4", "L3", "M1", "L2", "L1", "L3", "L2", "L4", "L1", "M5",
                "M1", "M3", "L3", "M4", "L2", "M3", "L3", "L1", "M2", "L2", "M4", "M4", "L4", "L3", "X",
                "L2", "M3", "M4", "L5", "M2", "M5", "L2", "L3", "L3", "M4", "L2", "M3", "L3", "L1", "M2",
                "L2", "M4", "M4", "L4", "L3", "L2", "M3", "M4", "L1", "L3", "L2", "L4", "L2", "L5", "L3",
                "S", "L4", "L1", "L2"],

            ["L3", "L4", "L5", "L3", "L1", "M1", "L2", "L4", "L3", "M5", "M5", "L1", "L1", "M3", "L5",
                "L3", "M1", "L3", "M2", "M2", "M1", "M5", "M2", "L2", "L4", "L3", "M4", "M5", "M1", "L2",
                "M1", "M4", "M2", "L4", "L4", "L2", "M3", "M1", "M5", "L2", "L1", "M2", "L4", "L3", "L4",
                "L2", "L1", "M1", "M3", "L4", "M2", "M3", "L5", "L4", "L3", "L2", "M3", "L5", "M4", "L3",
                "L1", "M5", "M5", "L4", "L4", "L2", "M4", "M5", "L1", "L4", "L5", "L5", "M5", "L1", "L2",
                "M3", "M5", "L1", "L3", "M4", "L2", "M1", "M2", "L3", "M4", "L2", "M2", "M3", "L1", "L3",
                "M2", "L4", "L4", "M1", "M1", "M3", "M4", "L4", "L3", "L1", "L2", "L2", "L4", "L3", "M5",
                "L5", "L2", "L1", "L5", "M3", "M5", "M5", "L1", "L3", "L1", "L3", "L2", "L5", "L4", "X",
                "L2", "L5", "L4", "L3", "L2", "L3", "L4", "L5", "L3", "L1", "M1", "L2", "L4", "L3", "M5",
                "M5", "L1", "L1", "M3", "L5", "L3", "M1", "L3", "M2", "M2", "M1", "M5", "M2", "L2", "L4",
                "L3", "M4", "M5", "M1", "L2", "M1", "M4", "M2", "L4", "L4", "L2", "M3", "M1", "M5", "L2",
                "L1", "M2", "L4", "L3", "S", "L2", "L1", "M1", "M3", "L4", "M2", "M3", "L5", "L4", "L3",
                "L2", "M3", "L5", "M4", "L3", "L1", "M5", "M5", "L4", "L4", "L2", "M4", "M5", "L1", "L4",
                "L5", "L5", "M5", "L1", "L2", "M3", "M5", "L1", "L3", "M4", "L2", "M1", "M2", "L3", "M4",
                "L2", "M2", "M3", "L1", "L3", "M2", "L4", "L4", "M1", "M1", "M3", "M4", "L4", "L3", "L1",
                "L2", "L2", "L4", "L3", "M5", "L5", "L2", "L1", "L5", "M3", "M5", "M5", "L1", "L3", "L1",
                "L3", "L2", "L5", "L4", "X", "L2", "L5", "L4", "L3", "L2"],

            ["L1", "L3", "L2", "L5", "M5", "L4", "X", "L2", "L4", "L3", "L1", "M2", "L1", "L2", "L3",
                "M3", "M4", "L3", "L5", "L2", "M2", "L1", "M5", "L4", "L3", "L2", "L4", "L1", "L1", "L3",
                "M4", "M3", "L2", "L1", "L4", "L3", "M5", "L1", "L2", "L4", "L4", "M1", "L2", "L5", "L1",
                "L3", "L2", "L5", "L4", "L1", "L2", "L4", "S", "L1", "L1", "L2", "L3", "L3", "L5", "L2",
                "L1", "L4", "L3", "L2"]


        ],
        "FREESPIN_MID_REELSET": [
            ["L1", "M3", "L2", "L3", "M4", "L4", "M5", "M3", "L3", "L1", "M4", "M5", "L1", "M1", "L3",
                "M3", "L2", "L4", "L2", "M5", "M1", "L3", "M4", "L1", "M2", "L2", "M5", "L5", "L4", "M5",
                "L2", "L1", "M4", "L4", "M4", "L3", "M3", "M2", "L5", "M1", "L2", "M5", "M3", "L1", "L4",
                "L3", "M4", "L5", "L1", "M3", "L2", "L2", "M4", "L4", "M2", "L2", "M5", "M5", "L1", "L3",
                "M1", "M2", "M4", "L4", "L3", "M5", "L5", "M2", "M1", "L1", "M4", "L5", "L3", "L4", "M5",
                "L1", "M3", "M2", "L5", "M5", "M1", "L1", "M4", "M5", "L4", "M5", "L1", "L2", "M2", "L3",
                "L5", "M5", "L4", "L2", "M3", "L1", "L3", "M2", "M5", "M4", "M4", "L1", "M1", "L2", "M3",
                "M5", "L3", "M2", "L4", "M4", "L2", "X", "M2", "L3", "L5", "M5", "L2", "L4", "L1", "M1",
                "L3", "M2", "L2", "M3", "X", "M4", "L3", "L2", "M5", "L5", "M2", "L1", "M1", "L3", "M3",
                "M5", "L5", "M4", "L4", "L3", "M4", "L1", "M2", "L3", "M3", "L2", "L4", "L1", "M5", "M2",
                "M5", "L2", "M3", "L5", "L1", "M4", "L4", "M1", "L3", "M3", "L5", "L1", "M3", "L2", "L3",
                "M4", "L4", "M5", "M3", "L3", "L1", "M4", "M5", "L1", "M1", "L3", "M3", "L2", "L4", "L2",
                "M5", "M1", "L3", "M4", "L1", "M2", "L2", "M5", "L5", "L4", "M5", "L2", "L1", "M4", "L4",
                "M4", "L3", "M3", "M2", "L5", "M1", "L2"],
            ["M3", "L2", "M4", "L1", "M1", "L5", "M2", "L1", "L4", "M5", "L1", "L2", "L3", "M4", "W",
                "L5", "M5", "L2", "M1", "L1", "L4", "M3", "L5", "M3", "L5", "M5", "L2", "M5", "L1", "M1",
                "L4", "M3", "L2", "M5", "L3", "M4", "M3", "L1", "M1", "L3", "M5", "M3", "L4", "M4", "M2",
                "L2", "M5", "M1", "L5", "X", "L4", "M3", "L2", "M5", "L1", "L3", "M3", "M2", "L3", "M3",
                "L4", "M4", "M1", "L4", "M3", "L2", "M2", "M4", "L1", "M1", "M4", "L4", "M3", "M5", "L5",
                "M3", "L2", "X", "L3", "M1", "L4", "M4", "L3", "L2", "L1", "L5", "L3", "M2", "L2", "L4",
                "X", "L1", "L5", "L3", "M5", "L1", "L4", "M1", "L5", "L3", "M3", "M2", "M3", "L1", "M1",
                "L1", "L3", "M4", "M2", "L3", "L2", "L1", "M1", "M3", "L3", "L4", "M4", "L5", "L4", "L2",
                "L3", "L4", "M5", "L2", "L4", "L5", "L1", "X", "L3", "L4", "M2", "L2", "L4", "L5", "L3",
                "M3", "L2", "L1", "L4", "M4", "M3", "M5", "L5", "M3", "L2", "X", "L3", "L4", "M4", "L3",
                "W", "L2", "L1", "L5", "L3", "L2", "L4", "M2", "L1", "M1", "L3", "M5", "L1", "L4", "M1",
                "L5", "L3", "M3", "M5", "M4", "M1", "L4", "M2", "M2", "M3", "L1", "M1", "M4", "M2", "L3",
                "L2", "X", "L1", "M1", "M3", "M4", "L5", "L4", "M2", "L3", "L4", "M5", "L2", "L4", "L5",
                "L1", "X", "L3", "L4", "M2", "L2", "L4", "L5", "L1", "L3", "M3", "L2", "L1", "L4"],
            ["M1", "L5", "M2", "L4", "L2", "M5", "L5", "L1", "L3", "M1", "L2", "W", "L1", "L3", "M4",
                "M5", "L4", "M3", "L2", "L5", "M1", "L1", "L3", "L2", "M5", "L3", "M2", "L4", "M4", "L2",
                "M5", "L1", "M1", "M3", "L5", "L3", "M2", "L2", "M5", "M4", "M3", "L3", "L1", "M4", "L3",
                "L4", "L1", "L2", "L2", "M1", "M5", "M5", "L3", "L3", "L4", "L5", "M2", "L2", "M3", "M1",
                "M5", "L3", "W", "L3", "M2", "L1", "L2", "W", "L4", "M3", "M1", "L1", "M4", "L5", "L3",
                "L3", "L2", "L4", "M1", "M5", "L3", "L3", "M3", "L1", "L1", "M4", "M2", "L2", "M5", "M3",
                "L2", "M1", "M4", "L1", "L4", "M5", "L2", "M2", "M1", "L3", "L2", "W", "L5", "M1", "L3",
                "L4", "L1", "L5", "L3", "M4", "M3", "L4", "X", "L1", "M2", "L3", "M3", "M3", "M4", "L3",
                "L2", "L4", "L1", "M4", "L3", "M1", "L2", "L1", "L3", "L2", "L4", "L1", "M5", "M1", "M3",
                "L3", "M4", "L2", "M3", "L3", "L1", "M2", "L2", "M4", "M4", "L4", "L3", "L2", "M3", "M4",
                "L5", "M2", "M5", "L2", "L3", "L3", "M4", "L2", "M3", "L3", "L1", "M2", "L2", "M4", "M4",
                "L4", "L3", "L2", "M3", "M4", "L5", "M2", "M5", "L2", "L3"],
            ["L3", "L4", "L5", "L1", "M1", "L2", "L4", "L3", "M5", "M5", "L1", "L1", "M3", "L5", "L3",
                "M1", "L3", "M2", "M2", "M1", "M5", "M2", "L2", "L4", "L3", "M4", "M5", "M1", "L2", "L4",
                "M4", "M2", "L4", "L3", "L5", "L4", "L2", "M3", "M1", "M5", "L2", "L1", "M2", "L4", "L3",
                "L4", "L5", "L1", "M1", "M3", "L4", "M2", "M3", "L4", "L3", "L2", "M3", "L5", "M4", "L3",
                "L1", "M5", "L4", "L4", "L2", "M4", "M5", "L1", "L4", "L5", "L5", "M5", "L1", "L2", "M3",
                "M5", "L1", "L3", "M4", "L2", "M1", "M2", "X", "M4", "L2", "L5", "M3", "L1", "L3", "M2",
                "L4", "L4", "M1", "M1", "M3", "M4", "L4", "L3", "L1", "L2", "L2", "L4", "L3", "M5", "L5",
                "L2", "L3", "L4", "L5", "L1", "M1", "L2", "L4", "L3", "M5", "M5", "L1", "L1", "M3", "L5",
                "L3", "M1", "L3", "M2", "M2", "M1", "M5", "M2", "L2", "L4", "L3", "M4", "M5", "M1", "L2",
                "L4", "M4", "M2", "L4", "L4", "L2", "M3", "M1", "M5", "L2", "L1", "M2", "L4", "L3", "L4",
                "L5", "L1", "M1", "M3", "L4", "M2", "M3", "L4", "L3", "L2", "M3", "L5", "M4", "L3", "L1",
                "W", "M5", "L4", "L4", "L2", "M4", "M5", "L1", "L4", "L5", "L5", "M5", "L1", "L2", "M3",
                "M5", "L1", "L3", "M4", "L2", "M1", "M2", "X", "M4", "L2", "L5", "M3", "L1", "L3", "M2",
                "L4", "L4", "M1", "M1", "M3", "M4", "L4", "L3", "L1", "L2", "L2", "L4", "L3", "M5", "L5",
                "L2"],
            ["L4", "L1", "L1", "L3", "M4", "M1", "M3", "M3", "L2", "L1", "L4", "L3", "M5", "M2", "L4",
                "M1", "M3", "M5", "L2", "L5", "M4", "L4", "L3", "L1", "L4", "M5", "M5", "M3", "L2", "M4",
                "L2", "M1", "M5", "L2", "L1", "M4", "M2", "X", "L3", "M5", "L1", "L2", "M5", "M2", "L1",
                "L2", "M2", "L3", "L1", "M4", "M5", "L4", "L5", "L3", "M5", "L2", "L1", "L1", "M1", "L2",
                "M3", "L4", "L1", "L3", "L5", "L2", "L1", "L2", "L4", "L2", "L4", "L1", "L5", "L1", "L3",
                "L2", "L5", "M5", "L4", "L1", "L2", "L4", "L3", "L1", "M2", "L1", "L2", "L3", "M3", "M4",
                "L3", "L5", "L2", "M2", "L1", "M5", "L4", "L3", "L2", "L4", "L1", "L1", "L3", "M4", "M3",
                "L2", "L1", "L4", "L3", "M5", "L1", "L2", "L4", "L4", "M1", "L2", "L5", "L1", "L3", "L2",
                "L5", "L4", "L1", "L2", "L4", "L3", "L1", "X", "L2", "L3", "L3", "L5", "L2", "L1", "L4",
                "L3", "L2"]
        ],
        "SUPERFS_REELSET": [
            ["L1", "L2", "L4", "MX", "MX", "L1", "L5", "MX", "L1", "MX", "L4", "L3", "L2", "MX", "L5",
                "L1", "L3", "MX", "L3", "L1", "L4", "MX", "L5", "L4", "L2", "MX", "L4", "L1", "L2", "MX",
                "L5", "L1", "L4", "MX", "L2", "L3", "L1", "MX", "L4", "L1", "L4", "MX", "L2", "L3", "L4",
                "MX", "L4", "L1", "MX", "MX", "L5", "L2", "L3", "MX", "L1", "L4", "L2", "MX", "MX", "L4",
                "L2", "MX", "L5", "L1", "L3", "MX", "L5", "L4", "L2", "MX", "L4", "L1", "L2", "MX", "L5",
                "L1", "L4", "MX", "L2", "L3", "L1", "MX", "L4", "L1", "L4", "MX", "L5", "L4", "L2", "MX",
                "X", "L1", "L2", "MX", "L5", "L1", "L4", "MX", "L2", "L3", "L1", "MX", "L4", "L1", "L4",
                "MX", "L5", "L4", "L2", "MX", "L4", "L1", "L2", "MX", "L5", "L1", "L4", "MX", "L2", "L3",
                "L1", "MX", "L4", "L1", "L4", "MX", "L2", "L1", "L3", "MX", "MX", "L2", "L1", "MX", "MX",
                "L5", "L2", "L3", "MX", "L2", "L1", "L5", "MX", "L4", "L3", "L1", "MX"],
            ["L1", "L2", "L4", "MX", "MX", "L1", "L5", "MX", "L1", "MX", "L4", "L3", "L2", "MX", "L5",
                "L1", "L3", "MX", "L3", "L1", "L4", "MX", "L5", "L4", "L2", "MX", "L4", "L1", "L2", "MX",
                "L5", "L1", "L4", "MX", "L2", "L3", "L1", "MX", "L4", "L1", "L4", "MX", "L2", "L3", "L4",
                "MX", "L4", "L4", "MX", "MX", "L5", "L2", "L3", "MX", "L1", "L4", "L2", "MX", "MX", "L4",
                "L2", "MX", "L5", "L1", "L3", "MX", "L5", "L4", "L2", "MX", "L4", "L1", "X", "MX", "L5",
                "L1", "L4", "MX", "L2", "L3", "L1", "MX", "L4", "W", "L1", "L4", "MX", "L5", "L4", "L2",
                "MX", "L4", "L1", "L2", "MX", "L5", "L1", "L4", "MX", "L2", "L3", "L1", "MX", "L4", "L1",
                "L4", "MX", "L5", "L4", "L2", "MX", "L4", "L1", "L2", "MX", "W", "L1", "L4", "MX", "L2",
                "L3", "L1", "MX", "L4", "L1", "L4", "MX", "L2", "L1", "L3", "MX", "MX", "L3", "L1", "MX",
                "MX", "L5", "L2", "L3", "MX", "L2", "L1", "L5", "MX", "L4", "L3", "L1", "MX"],
            ["L1", "L2", "L4", "MX", "MX", "L1", "L5", "MX", "L1", "MX", "L4", "L3", "L2", "MX", "L5",
                "L1", "L3", "MX", "L3", "L1", "L4", "MX", "L2", "L3", "L4", "MX", "L4", "L1", "MX", "MX",
                "L5", "L2", "L3", "MX", "L1", "L4", "L2", "MX", "MX", "L4", "L4", "MX", "L5", "L1", "L3",
                "MX", "L5", "L4", "L2", "MX", "L4", "L1", "L2", "MX", "L5", "L1", "L4", "MX", "L2", "W",
                "L3", "L1", "MX", "L4", "L1", "L4", "MX", "L5", "L4", "L2", "L4", "L1", "W", "L5", "L1",
                "L4", "L2", "L3", "L1", "L4", "L1", "L4", "L5", "L4", "L2", "MX", "L4", "L1", "L2", "MX",
                "L5", "L1", "L4", "MX", "L2", "L3", "L1", "MX", "L4", "L1", "L4", "MX", "L5", "L4", "L2",
                "MX", "L4", "L1", "L2", "MX", "L5", "L1", "L4", "MX", "L2", "L3", "L1", "MX", "L4", "L1",
                "L4", "MX", "L2", "L1", "L3", "MX", "MX", "L3", "L1", "MX", "MX", "L5", "L2", "L3", "MX",
                "L2", "L1", "L5", "MX", "L4", "L3", "L1", "MX", "L1", "L2", "L4", "MX", "MX", "L1", "L5",
                "MX", "L1", "MX", "L4", "L3", "L2", "MX", "L5", "L1", "L3", "MX", "L3", "L1", "L4", "MX",
                "L2", "L3", "L4", "MX", "L4", "L3", "MX", "MX", "L5", "L2", "L3", "MX", "L1", "L4", "L2",
                "MX", "MX", "L4", "L1", "MX", "L5", "L1", "L3", "MX", "L5", "L4", "L2", "MX", "L4", "L1",
                "L2", "MX", "L5", "L1", "L4", "MX", "L2", "L1", "L5", "L4", "L2", "L4", "L1", "L2", "X",
                "L5", "L1", "L4", "L2", "L3", "L1", "L4", "L1", "L4", "L3", "L1", "MX", "L4", "L1", "L4",
                "MX", "L5", "L4", "L2", "MX", "L4", "L1", "L2", "MX", "L5", "L1", "L4", "MX", "L2", "L3",
                "L1", "MX", "L4", "L1", "L4", "MX", "L5", "L4", "L2", "MX", "L4", "L1", "L2", "MX", "L5",
                "W", "L4", "MX", "L2", "L3", "L1", "MX", "L4", "L1", "L4", "MX", "L2", "L1", "L3", "X",
                "L5", "L2", "L3", "MX", "L2", "L1", "L5", "MX", "L4", "L3", "L1", "MX", "L1", "L2", "L4",
                "MX", "MX", "L1", "L5", "MX", "L1", "MX", "L4", "L3", "L2", "MX", "L5", "L1", "L3", "MX",
                "L3", "L1", "L4", "MX", "L2", "L3", "L4", "MX", "L4", "L1", "MX", "MX", "L5", "L2", "L3",
                "MX", "L1", "L4", "L2", "MX", "MX", "L4", "L4", "MX", "L5", "L1", "L3", "MX", "L5", "L4",
                "L2", "MX", "L4", "L1", "L2", "MX", "L5", "L1", "L4", "MX", "L2", "W", "L3", "L1", "MX",
                "L4", "L1", "L4", "MX", "L5", "L4", "L2", "L4", "L1", "L2", "L5", "L1", "L4", "L2", "L3",
                "L1", "L4", "L1", "L4", "L5", "L4", "L2", "MX", "L4", "L1", "L2", "MX", "L5", "L1", "L4",
                "MX", "L2", "L3", "L1", "MX", "L4", "L1", "L4", "MX", "L5", "L4", "L2", "MX", "L4", "L1",
                "L2", "MX", "L5", "L1", "L4", "MX", "L2", "W", "L1", "MX", "L4", "L1", "L4", "MX", "L2",
                "L1", "L3", "MX", "MX", "L3", "L1", "MX", "MX", "L5", "L2", "L3", "MX", "L2", "L1", "L5",
                "MX", "L4", "L3", "L1", "MX", "L1", "L2", "L4", "MX", "MX", "L1", "L5", "MX", "L1", "MX",
                "L4", "L3", "L2", "MX", "L5", "L1", "L3", "MX", "L3", "L1", "L4", "MX", "L2", "L3", "W",
                "MX", "L4", "L3", "MX", "MX", "L5", "L2", "L3", "MX", "L1", "L4", "L2", "MX", "MX", "L4",
                "L1", "MX", "L5", "L1", "L3", "MX", "L5", "L4", "L2", "MX", "L4", "L1", "L2", "MX", "L5",
                "L1", "L4", "MX", "L2", "L1", "L3", "L1", "MX", "L4", "L1", "L4", "MX", "L5", "L4", "L2",
                "MX", "L4", "L1", "L2", "MX", "L5", "L1", "W", "MX", "L2", "L3", "L1", "MX", "L4", "L1",
                "L4", "MX", "L5", "L4", "L2", "MX", "L4", "L1", "L2", "MX", "L5", "L1", "L4", "MX", "L2",
                "L3", "L1", "MX", "L4", "L1", "L4", "MX", "L2", "L1", "L3", "X", "L5", "L2", "L3", "MX",
                "L2", "L1", "L5", "MX", "L4", "L3", "L1", "MX"],
            ["L1", "L2", "L4", "MX", "MX", "L1", "L5", "MX", "L1", "MX", "L4", "L3", "L2", "MX", "L5",
                "L1", "L3", "MX", "L3", "L1", "L4", "MX", "L5", "L4", "L2", "L4", "L1", "L2", "W", "L5",
                "L1", "L4", "L2", "L3", "L1", "L4", "L1", "L4", "L5", "L4", "L2", "MX", "L4", "L1", "L2",
                "MX", "L5", "L1", "L4", "MX", "L2", "L3", "L1", "MX", "L4", "L1", "L4", "MX", "L5", "L4",
                "L2", "L4", "L1", "L2", "L5", "L1", "L4", "L2", "L3", "L1", "L4", "L1", "L4", "L2", "L3",
                "L4", "MX", "L4", "L3", "MX", "MX", "L5", "L2", "L3", "MX", "L1", "L4", "L2", "L5", "L1",
                "L3", "MX", "L5", "L4", "L2", "L4", "L1", "L2", "L5", "L1", "L4", "L2", "L3", "L1", "L4",
                "L1", "L4", "L5", "L4", "L2", "MX", "L4", "L1", "L2", "MX", "L5", "L1", "L4", "MX", "L2",
                "L3", "L1", "MX", "L4", "L1", "L4", "MX", "L5", "L4", "L2", "L4", "L1", "L2", "X", "L5",
                "L1", "L4", "W", "L2", "L3", "L1", "L4", "L1", "L4", "L5", "L4", "L2", "L4", "L1", "L2",
                "L5", "L1", "L4", "L2", "L3", "L1", "L4", "L1", "L4", "L5", "L4", "L2", "MX", "L4", "L1",
                "L2", "MX", "L5", "L1", "L4", "MX", "L2", "L3", "L1", "MX", "L4", "L1", "L4", "MX", "L5",
                "L4", "L2", "L4", "L1", "L2", "L5", "L1", "L3", "L4", "L2", "L3", "L1", "L4", "L1", "L4",
                "L5", "L4", "L2", "MX", "L4", "L1", "L2", "MX", "L5", "L1", "L4", "MX", "L2", "L3", "L1",
                "MX", "L4", "L1", "L4", "MX", "L2", "L1", "L3", "MX", "MX", "L4", "L1", "MX", "MX", "L5",
                "L2", "L3", "MX", "L2", "L1", "L5", "MX", "L4", "L3", "L1", "MX", "L5", "L4", "L2", "L4",
                "L1", "L2", "L5", "L1", "L4", "L2", "L3", "L1", "L4", "L1", "L4"],
            ["L1", "L2", "L4", "MX", "MX", "L1", "L5", "MX", "L1", "MX", "L4", "L3", "L2", "MX", "L5",
                "L1", "L3", "MX", "L3", "L1", "L4", "MX", "L5", "L4", "L2", "L4", "L1", "L2", "L5", "L1",
                "L4", "L2", "L3", "L1", "L4", "L1", "L4", "L5", "L4", "L2", "L4", "L1", "L2", "L5", "L1",
                "L4", "L2", "L3", "L1", "L4", "L1", "L4", "L5", "L4", "L2", "MX", "L4", "L1", "L2", "MX",
                "L5", "L1", "L4", "MX", "L2", "L3", "L1", "MX", "L4", "L1", "L4", "MX", "L5", "L4", "L2",
                "L4", "L1", "L2", "L5", "L1", "L4", "L2", "L3", "L1", "L4", "L1", "L4", "L2", "L3", "L4",
                "MX", "L4", "L1", "MX", "L5", "L2", "L3", "MX", "L1", "L4", "L2", "MX", "L5", "L4", "MX",
                "L5", "L1", "L3", "MX", "L5", "L4", "L2", "L4", "L1", "L2", "L5", "L1", "L4", "L2", "L3",
                "L1", "L4", "L1", "L4", "L5", "L4", "L2", "MX", "L4", "L1", "L2", "MX", "L5", "L1", "L4",
                "MX", "L2", "L3", "L1", "MX", "L4", "L1", "L4", "MX", "L5", "L4", "L2", "L4", "L1", "L2",
                "X", "L5", "L1", "L4", "L2", "L3", "L1", "L4", "L1", "L4", "L5", "L4", "L2", "L4", "L1",
                "L2", "L5", "L1", "L4", "L2", "L3", "L1", "L4", "L1", "L4", "L5", "L4", "L2", "MX", "L4",
                "L1", "L2", "MX", "L5", "L1", "L4", "MX", "L2", "L3", "L1", "MX", "L4", "L1", "L4", "MX",
                "L5", "L4", "L2", "L4", "L1", "L2", "L5", "L1", "L4", "L2", "L3", "L1", "L4", "L1", "L4",
                "L5", "L4", "L2", "L4", "L1", "L2", "L5", "L1", "L4", "L2", "L3", "L1", "L4", "L1", "L4",
                "L5", "L4", "L2", "MX", "L4", "L1", "L2", "MX", "L5", "L1", "L4", "MX", "L2", "L3", "L1",
                "MX", "L4", "L1", "L4", "MX", "L5", "L4", "L2", "L4", "L1", "L2", "L5", "L1", "L4", "L2",
                "L3", "L1", "L4", "L1", "L4", "L2", "L1", "L3", "MX", "MX", "L5", "L2", "MX", "MX", "L5",
                "L2", "L3", "MX", "L2", "L1", "L5", "MX", "L4", "L3", "L1", "MX", "L5", "L4", "L2", "L4",
                "L1", "L2", "L5", "L1", "L4", "L2", "L3", "L1", "L4", "L1", "L4"]
        ],
        "FREE_REELSET": [

            ["L1", "M3", "L2", "L3", "M4", "L4", "M5", "M3", "L3", "L1", "M4", "M5", "L1", "M1", "L3",
                "M3", "L2", "L4", "L2", "M5", "M1", "L3", "M4", "L1", "M2", "L2", "M5", "L5", "L4", "M5",
                "L2", "L1", "M4", "L4", "M4", "L3", "M3", "M2", "L5", "M1", "L2", "M5", "M3", "L1", "L4",
                "L3", "M4", "L5", "L1", "M3", "L2", "L2", "M4", "L4", "M2", "L2", "M5", "M5", "L1", "L3",
                "M1", "M2", "M4", "L4", "L3", "M5", "L5", "M2", "M1", "L1", "M4", "L5", "L3", "L4", "M5",
                "L1", "M3", "M2", "L5", "M5", "M1", "L1", "M4", "M5", "L4", "M5", "L1", "L2", "M2", "L3",
                "L5", "M5", "L4", "L2", "M3", "L1", "L3", "M2", "M5", "M4", "M4", "L1", "M1", "L2", "M3",
                "M5", "L3", "M2", "L4", "M4", "L2", "X", "M2", "L3", "L5", "M5", "L2", "L4", "L1", "M1",
                "L3", "M2", "L2", "M3", "X", "M4", "L3", "L2", "M5", "L5", "M2", "L1", "M1", "L3", "M3",
                "M5", "L5", "M4", "L4", "L3", "M4", "L1", "M2", "L3", "M3", "L2", "L4", "L1", "M5", "M2",
                "M5", "L2", "M3", "L5", "L1", "M4", "L4", "M1", "L3", "M3", "L5", "L1", "M3", "L2", "L3",
                "M4", "L4", "M5", "M3", "L3", "L1", "M4", "M5", "L1", "M1", "L3", "M3", "L2", "L4", "L2",
                "M5", "M1", "L3", "M4", "L1", "M2", "L2", "M5", "L5", "L4", "M5", "L2", "L1", "M4", "L4",
                "M4", "L3", "M3", "M2", "L5", "M1", "L2"],

            ["M3", "L2", "M4", "L1", "M1", "L5", "M2", "L1", "L4", "M5", "L1", "L2", "L3", "M4", "W",
                "L5", "M5", "L2", "M1", "L1", "L4", "M3", "L5", "M3", "L5", "M5", "L2", "M5", "L1", "M1",
                "L4", "M3", "L2", "M5", "L3", "M4", "M3", "L1", "M1", "L3", "M5", "M3", "L4", "M4", "M2",
                "L2", "M5", "M1", "L5", "X", "L4", "M3", "L2", "M5", "L1", "L3", "M3", "M2", "L3", "M3",
                "L4", "M4", "M1", "L4", "M3", "L2", "M2", "M4", "L1", "M1", "M4", "L4", "M3", "M5", "L5",
                "M3", "L2", "X", "L3", "M1", "L4", "M4", "L3", "L2", "L1", "L5", "L3", "M2", "L2", "L4",
                "X", "L1", "L5", "L3", "M5", "L1", "L4", "M1", "L5", "L3", "M3", "M2", "M3", "L1", "M1",
                "L1", "L3", "M4", "M2", "L3", "L2", "L1", "M1", "M3", "L3", "L4", "M4", "L5", "L4", "L2",
                "L3", "L4", "M5", "L2", "L4", "L5", "L1", "X", "L3", "L4", "M2", "L2", "L4", "L5", "L3",
                "M3", "L2", "L1", "L4", "M4", "M3", "M5", "L5", "M3", "L2", "X", "L3", "L4", "M4", "L3",
                "W", "L2", "L1", "L5", "L3", "L2", "L4", "M2", "L1", "M1", "L3", "M5", "L1", "L4", "M1",
                "L5", "L3", "M3", "M5", "M4", "M1", "L4", "M2", "M2", "M3", "L1", "M1", "M4", "M2", "L3",
                "L2", "X", "L1", "M1", "M3", "M4", "L5", "L4", "M2", "L3", "L4", "M5", "L2", "L4", "L5",
                "L1", "X", "L3", "L4", "M2", "L2", "L4", "L5", "L1", "L3", "M3", "L2", "L1", "L4"],

            ["M1", "L5", "M2", "L4", "L2", "M5", "L5", "L1", "L3", "M1", "L2", "W", "L1", "L3", "M4",
                "M5", "L4", "M3", "L2", "L5", "M1", "L1", "L3", "L2", "M5", "L3", "M2", "L4", "M4", "L2",
                "M5", "L1", "M1", "M3", "L5", "L3", "M2", "L2", "M5", "M4", "M3", "L3", "L1", "M4", "L3",
                "L4", "L1", "L2", "L2", "M1", "M5", "M5", "L3", "L3", "L4", "L5", "M2", "L2", "M3", "M1",
                "M5", "L3", "W", "L3", "M2", "L1", "L2", "W", "L4", "M3", "M1", "L1", "M4", "L5", "L3",
                "L3", "L2", "L4", "M1", "M5", "L3", "L3", "M3", "L1", "L1", "M4", "M2", "L2", "M5", "M3",
                "L2", "M1", "M4", "L1", "L4", "M5", "L2", "M2", "M1", "L3", "L2", "W", "L5", "M1", "L3",
                "L4", "L1", "L5", "L3", "M4", "M3", "L4", "X", "L1", "M2", "L3", "M3", "M3", "M4", "L3",
                "L2", "L4", "L1", "M4", "L3", "M1", "L2", "L1", "L3", "L2", "L4", "L1", "M5", "M1", "M3",
                "L3", "M4", "L2", "M3", "L3", "L1", "M2", "L2", "M4", "M4", "L4", "L3", "L2", "M3", "M4",
                "L5", "M2", "M5", "L2", "L3", "L3", "M4", "L2", "M3", "L3", "L1", "M2", "L2", "M4", "M4",
                "L4", "L3", "L2", "M3", "M4", "L5", "M2", "M5", "L2", "L3"],

            ["L3", "L4", "L5", "L1", "M1", "L2", "L4", "L3", "M5", "M5", "L1", "L1", "M3", "L5", "L3",
                "M1", "L3", "M2", "M2", "M1", "M5", "M2", "L2", "L4", "L3", "M4", "M5", "M1", "L2", "L4",
                "M4", "M2", "L4", "L3", "L5", "L4", "L2", "M3", "M1", "M5", "L2", "L1", "M2", "L4", "L3",
                "L4", "L5", "L1", "M1", "M3", "L4", "M2", "M3", "L4", "L3", "L2", "M3", "L5", "M4", "L3",
                "L1", "M5", "L4", "L4", "L2", "M4", "M5", "L1", "L4", "L5", "L5", "M5", "L1", "L2", "M3",
                "M5", "L1", "L3", "M4", "L2", "M1", "M2", "X", "M4", "L2", "L5", "M3", "L1", "L3", "M2",
                "L4", "L4", "M1", "M1", "M3", "M4", "L4", "L3", "L1", "L2", "L2", "L4", "L3", "M5", "L5",
                "L2", "L3", "L4", "L5", "L1", "M1", "L2", "L4", "L3", "M5", "M5", "L1", "L1", "M3", "L5",
                "L3", "M1", "L3", "M2", "M2", "M1", "M5", "M2", "L2", "L4", "L3", "M4", "M5", "M1", "L2",
                "L4", "M4", "M2", "L4", "L4", "L2", "M3", "M1", "M5", "L2", "L1", "M2", "L4", "L3", "L4",
                "L5", "L1", "M1", "M3", "L4", "M2", "M3", "L4", "L3", "L2", "M3", "L5", "M4", "L3", "L1",
                "W", "M5", "L4", "L4", "L2", "M4", "M5", "L1", "L4", "L5", "L5", "M5", "L1", "L2", "M3",
                "M5", "L1", "L3", "M4", "L2", "M1", "M2", "X", "M4", "L2", "L5", "M3", "L1", "L3", "M2",
                "L4", "L4", "M1", "M1", "M3", "M4", "L4", "L3", "L1", "L2", "L2", "L4", "L3", "M5", "L5",
                "L2"],

            ["L4", "L1", "L1", "L3", "M4", "M1", "M3", "M3", "L2", "L1", "L4", "L3", "M5", "M2", "L4",
                "M1", "M3", "M5", "L2", "L5", "M4", "L4", "L3", "L1", "L4", "M5", "M5", "M3", "L2", "M4",
                "L2", "M1", "M5", "L2", "L1", "M4", "M2", "X", "L3", "M5", "L1", "L2", "M5", "M2", "L1",
                "L2", "M2", "L3", "L1", "M4", "M5", "L4", "L5", "L3", "M5", "L2", "L1", "L1", "M1", "L2",
                "M3", "L4", "L1", "L3", "L5", "L2", "L1", "L2", "L4", "L2", "L4", "L1", "L5", "L1", "L3",
                "L2", "L5", "M5", "L4", "L1", "L2", "L4", "L3", "L1", "M2", "L1", "L2", "L3", "M3", "M4",
                "L3", "L5", "L2", "M2", "L1", "M5", "L4", "L3", "L2", "L4", "L1", "L1", "L3", "M4", "M3",
                "L2", "L1", "L4", "L3", "M5", "L1", "L2", "L4", "L4", "M1", "L2", "L5", "L1", "L3", "L2",
                "L5", "L4", "L1", "L2", "L4", "L3", "L1", "X", "L2", "L3", "L3", "L5", "L2", "L1", "L4",
                "L3", "L2"]


        ]
    };
    WIN_RATIOS: IHorribleHeroesWinRatios = {
        normalWin: [
            {
                ratio: 0,
                text: "Tiny Win",
                duration: 0,
                type: WinRatioType.TINY,
                sound: HorribleHeroesSoundConfig.WIN_TINY
            },
            {
                ratio: 1.5,
                text: "Small Win",
                duration: 0,
                type: WinRatioType.SMALL,
                sound: HorribleHeroesSoundConfig.WIN_SMALL
            },
            {
                ratio: 3,
                text: "Medium Win",
                duration: 0.5,
                type: WinRatioType.MEDIUM,
                sound: HorribleHeroesSoundConfig.WIN_MEDIUM
            },
            {
                ratio: 5,
                text: "Large Win",
                duration: 0.8,
                type: WinRatioType.LARGE,
                sound: HorribleHeroesSoundConfig.WIN_LARGE
            }
        ],
        bigWin: [
            {
                level: 1,
                ratio: 15,
                text: "BigWin1",
                duration: 4.344,
                type: WinRatioType.BIG_WIN,
                sound: HorribleHeroesSoundConfig.BIG_WIN,
            },
            {
                level: 2,
                ratio: 25,
                text: "BigWin2",
                duration: 4.344,
                type: WinRatioType.MEGA_WIN,
                sound: HorribleHeroesSoundConfig.MEGA_WIN

            },
            {
                level: 3,
                ratio: 50,
                text: "BigWin3",
                duration: 4.344,
                type: WinRatioType.SUPER_MEGA_WIN,
                sound: HorribleHeroesSoundConfig.SUPER_MEGA_WIN
            },
            {
                level: 4,
                ratio: 100,
                text: "BigWin4",
                duration: 4.344,
                type: WinRatioType.SUPER_SUPER_MEGA_WIN,
                sound: HorribleHeroesSoundConfig.SUPER_MEGA_WIN
            },
            {
                level: 5,
                ratio: 250,
                text: "BigWin5",
                duration: 8.688,
                type: WinRatioType.SUPER_DUPER_MEGA_WIN,
                sound: HorribleHeroesSoundConfig.SUPER_MEGA_WIN
            }
        ]
    };
    PERFORMANCE_LOGGER: boolean = true;

    BONUS_MODES: string[] = [HorribleHeroesGameMode.FREESPIN_LOW, HorribleHeroesGameMode.FREESPIN_MID];

    ABORT_DELAY: number = 0; // #318 -> 1st point

    // #543
    FS_TRIGGER_DELAY: number = 0;

    // #548
    WIN_ABORT_DELAY: number = 0;

    // #582
    WIN_BELOW_STAKE: boolean = false;

    private constructor() {
        if (HorribleHeroesGameConfig.instance) {
            throw new Error("HorribleHeroesGameConfig.constructor() - Instantiation failed: Singleton.");
        }
    }
}